import React, { useMemo, useContext, useState, useCallback, memo } from "react";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { Box, Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { AuthContext } from "../context";
import jsPDF from "jspdf";
import { useNavigate } from "react-router-dom";

import { MRT_Localization_IT } from "material-react-table/locales/it";
import * as XLSX from "xlsx";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TextSnippetIcon from "@mui/icons-material/TextSnippet";
import AddIcon from "@mui/icons-material/Add";
const GenericTable = memo((props) => {
	const {
		columns = [],
		data = [],
		onDelete = null,
		onEdit = null,
		renderDetailPanel, // Nuova prop per il dettaglio personalizzato
		pivotTable = false, // Flag per abilitare la modalità pivot
		authModelName,
		enableRowSelection = false,
		defualtGrouping = [],
		enableColumnFilterModes = true,
		showColumnFiltersInitially = true,
		enableSorting = true,
		enableColumnActions = true,
		disableButton,
		initialSelectedAll = false,
		addRowFunction,
		isAddRowEnabled = false,
		sortingKey = "name",
		headerHeight,
	} = props;
	const authContext = useContext(AuthContext);
	const [exportDialogOpen, setExportDialogOpen] = useState(false);
	const [exportType, setExportType] = useState(null);
	const navigate = useNavigate();

	// Memoize auth permissions to avoid recalculation
	const authPermissions = useMemo(
		() => ({
			canView: authContext?.hasAuth(`VIEW_${authModelName}`) || authModelName === undefined,
			canAdd: authContext?.hasAuth(`ADD_${authModelName}`),
			canEdit:
				authContext?.hasAuth(`EDIT_${authModelName}`) ||
				authContext?.hasAuth(`UPSERT_${authModelName}`) ||
				authContext?.hasAuth(`UPDATE_${authModelName}`),
			canDelete: authContext?.hasAuth(`DELETE_${authModelName}`) || authContext?.hasAuth(`UPSERT_${authModelName}`),
		}),
		[authContext, authModelName]
	);

	// Memoize default add function
	const defaultAddFunction = useCallback(() => navigate("add"), [navigate]);

	// Memoize action handlers
	const handleEdit = useCallback((id) => onEdit?.(id), [onEdit]);
	const handleDelete = useCallback((id) => onDelete?.(id), [onDelete]);

	// Static styles to avoid re-creation
	const actionBoxStyle = useMemo(() => ({ display: "flex", gap: "8px" }), []);

	// Aggiungi una colonna per il grouping se pivotTable è abilitato
	const columnsWithActions = useMemo(() => {
		const baseColumns = columns || [];
		const actionColumn =
			(onEdit || onDelete) && (authPermissions.canEdit || authPermissions.canDelete)
				? {
						id: "actions",
						header: "Azioni",
						Cell: ({ row }) => (
							<Box sx={actionBoxStyle}>
								{onEdit && authPermissions.canEdit && (
									<IconButton onClick={() => handleEdit(row.original.id)} size="small">
										<EditIcon />
									</IconButton>
								)}
								{onDelete && authPermissions.canDelete && (
									<IconButton onClick={() => handleDelete(row.original.id)} size="small">
										<DeleteIcon />
									</IconButton>
								)}
							</Box>
						),
						size: 100,
					}
				: null;

		return [...baseColumns, ...(actionColumn ? [actionColumn] : [])];
	}, [columns, onEdit, onDelete, authPermissions, handleEdit, handleDelete, actionBoxStyle]);

	const handleExportClick = useCallback((type) => {
		setExportType(type);
		setExportDialogOpen(true);
	}, []);

	const handleExportConfirm = useCallback(
		(exportOption) => {
			setExportDialogOpen(false);
			if (exportType === "pdf") {
				handleExportPDF(exportOption);
			} else if (exportType === "xlsx") {
				handleExportXLSX(exportOption);
			}
		},
		[exportType]
	);

	const handleCloseExportDialog = useCallback(() => {
		setExportDialogOpen(false);
	}, []);

	const handleExportPDF = (exportOption) => {
		let rowsToExport;
		switch (exportOption) {
			case "all":
				rowsToExport = data;
				break;
			case "visible":
				rowsToExport = table.getRowModel().rows.map((row) => row.original);
				break;
			case "selected":
				rowsToExport = table.getSelectedRowModel().rows.map((row) => row.original);
				break;
			default:
				return;
		}

		const doc = new jsPDF();
		const tableColumnHeaders = columns.map((column) => column.header);
		const tableRows = rowsToExport.map((row) =>
			columns.map((column) => {
				if (column.cellAttachment) {
					return row[column.cellAttachment];
				}
				return row[column.id];
			})
		);

		doc.text("Exported Data", 14, 10);
		doc.autoTable({
			head: [tableColumnHeaders],
			body: tableRows,
			startY: 20,
		});
		doc.save("exported_data.pdf");
	};

	const handleExportXLSX = (exportOption) => {
		let rowsToExport;
		switch (exportOption) {
			case "all":
				rowsToExport = data;
				break;
			case "visible":
				rowsToExport = table.getRowModel().rows.map((row) => row.original);
				break;
			case "selected":
				rowsToExport = table.getSelectedRowModel().rows.map((row) => row.original);
				break;
			default:
				return;
		}

		// Crea un array di oggetti con i nomi delle colonne corretti
		const exportData = rowsToExport.map((row) =>
			columns.reduce((acc, column) => {
				acc[column.header] = row[column.id];
				return acc;
			}, {})
		);

		// Crea il foglio di lavoro
		const worksheet = XLSX.utils.json_to_sheet(exportData);

		// Applica lo stile all'intestazione
		const range = XLSX.utils.decode_range(worksheet["!ref"]);
		for (let C = range.s.c; C <= range.e.c; ++C) {
			const address = XLSX.utils.encode_col(C) + "1"; // Colonna + riga 1
			if (!worksheet[address]) continue;
			worksheet[address].s = {
				font: { bold: true },
				fill: { fgColor: { rgb: "FFFFAA00" } }, // Colore di sfondo arancione
				alignment: { horizontal: "center" },
			};
		}

		// Crea il workbook e aggiungi il foglio
		const workbook = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

		// Esporta il file
		XLSX.writeFile(workbook, "exported_data.xlsx");
	};

	// Memoize toolbar styles
	const toolbarBoxStyle = useMemo(
		() => ({
			display: "flex",
			gap: "16px",
			padding: "8px",
			flexWrap: "wrap",
		}),
		[]
	);

	const iconStyle = useMemo(
		() => ({
			height: "24px",
			width: "24px",
			marginRight: "10px",
		}),
		[]
	);

	// Memoize export handlers
	const handlePdfExport = useCallback(() => handleExportClick("pdf"), [handleExportClick]);
	const handleXlsxExport = useCallback(() => handleExportClick("xlsx"), [handleExportClick]);

	// Optimize toolbar function
	const actualAddRowFunction = addRowFunction || defaultAddFunction;

	// Modify the renderTopToolbarCustomActions
	const renderTopToolbarCustomActions = useCallback(
		({ table }) =>
			!disableButton && (
				<Box sx={toolbarBoxStyle}>
					{authPermissions.canAdd && isAddRowEnabled && (
						<Button onClick={actualAddRowFunction}>
							<AddIcon style={iconStyle} />
							AGGIUNGI
						</Button>
					)}

					<Button onClick={handlePdfExport}>
						<PictureAsPdfIcon style={iconStyle} />
						Esporta PDF
					</Button>
					<Button onClick={handleXlsxExport}>
						<TextSnippetIcon style={iconStyle} />
						Esporta XLSX
					</Button>
				</Box>
			),
		[
			disableButton,
			toolbarBoxStyle,
			authPermissions.canAdd,
			isAddRowEnabled,
			actualAddRowFunction,
			iconStyle,
			handlePdfExport,
			handleXlsxExport,
		]
	);

	// Memoize export handlers
	const handleExportAll = useCallback(() => handleExportConfirm("all"), [handleExportConfirm]);
	const handleExportVisible = useCallback(() => handleExportConfirm("visible"), [handleExportConfirm]);
	const handleExportSelected = useCallback(() => handleExportConfirm("selected"), [handleExportConfirm]);

	// Memoize initial state to prevent re-creation
	const initialState = useMemo(
		() => ({
			showColumnFilters: showColumnFiltersInitially,
			grouping: defualtGrouping,
			density: "compact",
			pagination: {
				pageIndex: 0,
				pageSize: 50,
			},
			rowSelection: initialSelectedAll
				? data.reduce((acc, _, index) => {
						acc[index] = true;
						return acc;
					}, {})
				: {},
			sorting: [
				{
					id: sortingKey,
					desc: false,
				},
			],
		}),
		[showColumnFiltersInitially, defualtGrouping, initialSelectedAll, data, sortingKey]
	);

	// Memoize getSubRows function
	const getSubRows = useCallback((row) => row.subRows, []);

	// Memoize renderDetailPanel
	const memoizedRenderDetailPanel = useMemo(() => {
		return pivotTable && renderDetailPanel ? ({ row }) => renderDetailPanel(row.original) : undefined;
	}, [pivotTable, renderDetailPanel]);

	const globalHeadCellProps = useMemo(() => {
		if (!headerHeight) return undefined;
		return {
			sx: {
				height: headerHeight,
				minHeight: headerHeight,
				maxHeight: headerHeight,
			},
		};
	}, [headerHeight]);

	// Configurazione tabella
	const table = useMaterialReactTable({
		columns: columnsWithActions,
		data,
		enableRowSelection,
		enableColumnFilterModes,
		enableExpanding: pivotTable,
		enableGrouping: pivotTable,
		enableSorting,
		enableColumnActions,
		localization: MRT_Localization_IT,
		getSubRows,
		initialState,
		muiTableHeadCellProps: globalHeadCellProps,
		renderDetailPanel: memoizedRenderDetailPanel,
		renderTopToolbarCustomActions,
	});

	// Add the export dialog - memoized component (after table initialization)
	const ExportDialog = useMemo(
		() => (
			<Dialog open={exportDialogOpen} onClose={handleCloseExportDialog}>
				<DialogTitle>Esporta dati</DialogTitle>
				<DialogContent>
					<Button onClick={handleExportAll}>Esporta tutte le righe</Button>
					<Button onClick={handleExportVisible}>Esporta righe visibili</Button>
					<Button onClick={handleExportSelected} disabled={!table?.getIsSomeRowsSelected?.() && !table?.getIsAllRowsSelected?.()}>
						Esporta righe selezionate
					</Button>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseExportDialog}>Annulla</Button>
				</DialogActions>
			</Dialog>
		),
		[exportDialogOpen, handleCloseExportDialog, handleExportAll, handleExportVisible, handleExportSelected, table]
	);

	// Early return if no view permission
	if (!authPermissions.canView) {
		return null;
	}

	return (
		<>
			<MaterialReactTable table={table} key={props?.key} />
			{ExportDialog}
		</>
	);
});

export default GenericTable;
